import { CheckCircle, Clock, XCircle } from 'lucide-react';

import type { ScenarioRun } from '@/lib/types/rome';
import { formatDurationPrecise } from '@/lib/utils/date';

// ─── Tag convention constants ────────────────────────────────────────────────

export const SUITE_TAG_PREFIX = 'suite:';
export const SUITE_RUN_TAG_PREFIX = 'suite_run:';

// Legacy prefixes for backward compatibility with existing ES data
export const LEGACY_GROUP_TAG_PREFIX = 'group:';
export const LEGACY_INSTANCE_TAG_PREFIX = 'instance:';

// ─── Tag extraction helpers ──────────────────────────────────────────────────

/** Extract the suite tag value from a scenario run's tags (supports legacy group: prefix) */
export function extractSuiteTag(run: ScenarioRun): string | null {
  return run.tags?.find((t) => t.startsWith(SUITE_TAG_PREFIX) || t.startsWith(LEGACY_GROUP_TAG_PREFIX)) ?? null;
}

/** Extract the suite run tag value from a scenario run's tags (supports legacy instance: prefix) */
export function extractSuiteRunTag(run: ScenarioRun): string | null {
  return run.tags?.find((t) => t.startsWith(SUITE_RUN_TAG_PREFIX) || t.startsWith(LEGACY_INSTANCE_TAG_PREFIX)) ?? null;
}

/** Strip the prefix from a tag to get the human-readable name */
export function tagDisplayName(tag: string): string {
  if (tag.startsWith(SUITE_TAG_PREFIX)) return tag.slice(SUITE_TAG_PREFIX.length);
  if (tag.startsWith(LEGACY_GROUP_TAG_PREFIX)) return tag.slice(LEGACY_GROUP_TAG_PREFIX.length);
  if (tag.startsWith(SUITE_RUN_TAG_PREFIX)) return tag.slice(SUITE_RUN_TAG_PREFIX.length);
  if (tag.startsWith(LEGACY_INSTANCE_TAG_PREFIX)) return tag.slice(LEGACY_INSTANCE_TAG_PREFIX.length);
  return tag;
}

/** Format a suite tag into a display-friendly name */
export function formatSuiteName(suiteTag: string): string {
  return tagDisplayName(suiteTag)
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── Run duration ────────────────────────────────────────────────────────────

const isValidDate = (date: Date): boolean => !Number.isNaN(date.getTime());

export function getRunDuration(run: ScenarioRun): string {
  if (run.duration_ms != null && run.duration_ms >= 0) {
    return formatDurationPrecise(run.duration_ms);
  }
  if (!run.started_at || !run.completed_at) return '-';
  try {
    const start = new Date(run.started_at);
    const end = new Date(run.completed_at);
    if (!isValidDate(start) || !isValidDate(end)) return '-';
    const durationMs = end.getTime() - start.getTime();
    if (durationMs >= 0) return formatDurationPrecise(durationMs);
  } catch {
    // Fall through
  }
  return '-';
}

// ─── Display helpers ─────────────────────────────────────────────────────────

export function getUserDisplay(run: ScenarioRun): string {
  if (run.user_name) return run.user_name;
  if (run.user_email) return run.user_email;
  if (run.user_id) {
    const parts = run.user_id.split('|');
    return parts[parts.length - 1] || run.user_id;
  }
  return '-';
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(2)} GB`;
}

// ─── Outcome styling ─────────────────────────────────────────────────────────

export function getOutcomeConfig(outcome: string | null | undefined): {
  icon: typeof CheckCircle;
  color: string;
  bg: string;
  border: string;
  dot: string;
  label: string;
} {
  switch (outcome) {
    case 'success':
      return {
        icon: CheckCircle,
        color: 'text-emerald-600 dark:text-emerald-500',
        bg: 'bg-emerald-50 dark:bg-emerald-500/10',
        border: 'border-emerald-200 dark:border-emerald-500/20',
        dot: 'bg-emerald-500',
        label: 'Passed',
      };
    case 'failure':
      return {
        icon: XCircle,
        color: 'text-red-600 dark:text-red-500',
        bg: 'bg-red-50 dark:bg-red-500/10',
        border: 'border-red-200 dark:border-red-500/20',
        dot: 'bg-red-500',
        label: 'Failed',
      };
    case 'error':
      return {
        icon: XCircle,
        color: 'text-orange-600 dark:text-orange-500',
        bg: 'bg-orange-50 dark:bg-orange-500/10',
        border: 'border-orange-200 dark:border-orange-500/20',
        dot: 'bg-orange-500',
        label: 'Error',
      };
    case 'timeout':
      return {
        icon: Clock,
        color: 'text-amber-600 dark:text-amber-500',
        bg: 'bg-amber-50 dark:bg-amber-500/10',
        border: 'border-amber-200 dark:border-amber-500/20',
        dot: 'bg-amber-500',
        label: 'Timeout',
      };
    case 'cancelled':
      return {
        icon: XCircle,
        color: 'text-gray-500 dark:text-gray-400',
        bg: 'bg-gray-50 dark:bg-gray-500/10',
        border: 'border-gray-200 dark:border-gray-500/20',
        dot: 'bg-gray-400',
        label: 'Cancelled',
      };
    default:
      return {
        icon: XCircle,
        color: 'text-gray-500 dark:text-gray-400',
        bg: 'bg-gray-50 dark:bg-gray-500/10',
        border: 'border-gray-200 dark:border-gray-500/20',
        dot: 'bg-gray-400',
        label: outcome || 'Unknown',
      };
  }
}
